<template>
  <div class="balance-card">
    <!-- Skeleton Loader -->
    <template v-if="loading">
      <div class="skeleton-text skeleton-label"></div>
      <div class="skeleton-amount-container">
        <div class="skeleton-text skeleton-amount"></div>
        <div class="skeleton-toggle"></div>
      </div>
      <div class="balance-actions">
        <div class="skeleton-button"></div>
        <div class="skeleton-button"></div>
      </div>
    </template>

    <template v-else>
      <div class="balance-label">{{ label }}</div>

      <!-- Virtual Account not generated yet -->
      <div v-if="needsVirtualAccount" class="generate-va">
        <div class="generate-va-text">
          <div class="generate-va-title">Generate Virtual Account</div>
          <div class="generate-va-desc">
            Generate Virtual Account to top up your {{ label }} via bank transfer.
          </div>
        </div>
        <button class="btn-primary-green" :disabled="generating" @click="$emit('generate-va')">
          {{ generating ? "Generating..." : "Generate Now" }}
        </button>
      </div>

      <template v-else>
        <div class="balance-amount">
          <span>IDR {{ formattedBalance }}</span>
          <button class="eye-btn" type="button" :aria-label="visible ? 'Hide balance' : 'Show balance'"
            @click="visible = !visible">
            <svg v-if="visible" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68M6.61 6.61A13.53 13.53 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61M2 2l20 20"
                stroke="#022326" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" stroke="#022326" stroke-width="1.5"
                stroke-linecap="round" stroke-linejoin="round" />
              <circle cx="12" cy="12" r="3" stroke="#022326" stroke-width="1.5" />
            </svg>
          </button>
        </div>

        <div class="balance-actions">
          <button v-if="canShowAddBalance" class="btn-primary-green" @click="$emit('add-balance')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
            Add Balance
          </button>
          <button class="btn-transfer-balance" @click="$emit('transfer-balance')">
            Transfer Balance
          </button>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { canAddBalance, isAkunDemo } from "@/utils/role-utils";

export default {
  name: "BalanceCard",
  emits: ["add-balance", "transfer-balance", "generate-va"],
  props: {
    label: {
      type: String,
      required: true,
    },
    // null means the balance is not available yet (rendered as "IDR -")
    balance: {
      type: Number,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    needsVirtualAccount: {
      type: Boolean,
      default: false,
    },
    generating: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      visible: true,
    };
  },
  computed: {
    formattedBalance() {
      if (this.balance === null || this.balance === undefined) return "-";
      if (!this.visible) return "••••••••";
      return this.balance.toLocaleString("en-US");
    },
    canShowAddBalance() {
      // Hide add balance button if company_name === 'AKUN DEMO'
      if (isAkunDemo()) {
        return false;
      }
      return canAddBalance();
    },
  },
};
</script>

<style scoped>
.balance-card {
  background: #fff;
  border: 1px solid #dae0e2;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  height: 100%;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  color: #022326;
}

.balance-label {
  font-size: 14px;
  line-height: 18px;
  color: #022326;
}

.balance-amount {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 28px;
  line-height: 32px;
  font-weight: 700;
  color: #022326;
  margin-top: 4px;
}

.eye-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid #d0d5dd;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
}

.balance-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
}

.btn-primary-green,
.btn-transfer-balance {
  height: 40px;
  padding: 8px 16px;
  border-radius: 4px;
  font-family: inherit;
  font-size: 16px;
  line-height: 24px;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
}

.btn-primary-green {
  background: linear-gradient(180deg, #43bb75 0%, #1c9762 100%);
  border: 1px solid #128050;
  box-shadow: inset 0 2px 1px 0 #ffffff40;
  color: #fff;
}

.btn-primary-green:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-transfer-balance {
  background: #fff;
  border: 1px solid #128050;
  color: #022326;
}

.btn-transfer-balance:hover {
  background: #128050;
  color: #fff;
}

/* Generate Virtual Account state */
.generate-va {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 16px;
  padding: 16px;
  border-radius: 8px;
  background: #e8f8ee;
}

.generate-va-title {
  font-size: 14px;
  line-height: 18px;
  font-weight: 600;
  color: #022326;
}

.generate-va-desc {
  font-size: 12px;
  line-height: 18px;
  color: #022326;
  margin-top: 4px;
  max-width: 276px;
}

@media (max-width: 480px) {
  .balance-amount {
    font-size: 22px;
    line-height: 28px;
  }

  .generate-va {
    flex-direction: column;
    align-items: flex-start;
  }
}

/* Skeleton Loader Styles */
.skeleton-text,
.skeleton-button,
.skeleton-toggle {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

.skeleton-label {
  width: 120px;
  height: 16px;
  margin-bottom: 8px;
}

.skeleton-amount-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.skeleton-amount {
  width: 208px;
  height: 32px;
}

.skeleton-toggle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

.skeleton-button {
  width: 142px;
  height: 40px;
  border-radius: 4px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }

  100% {
    background-position: 200% 0;
  }
}
</style>
